import { Routes, Route } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import LoadingSpinner from './components/LoadingSpinner';
import InstallPrompt from './components/InstallPrompt';
import ErrorBoundary from './components/ErrorBoundary';
import { supabase } from './services/supabase';

const MenuPage = lazy(() => import('./pages/MenuPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const ConfirmationPage = lazy(() => import('./pages/ConfirmationPage'));
const MyOrdersPage = lazy(() => import('./pages/MyOrdersPage'));
const OrderHistoryPage = lazy(() => import('./pages/OrderHistoryPage'));
const ReviewPromptPage = lazy(() => import('./pages/ReviewPromptPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const SplitBillModePage = lazy(() => import('./pages/SplitBillModePage'));
const SplitEqualPage = lazy(() => import('./pages/SplitEqualPage'));
const SplitByItemPage = lazy(() => import('./pages/SplitByItemPage'));
const SplitCustomPage = lazy(() => import('./pages/SplitCustomPage'));
const NFCWaitingPage = lazy(() => import('./pages/NFCWaitingPage'));
const GiftCardPurchasePage = lazy(() => import('./pages/GiftCardPurchasePage'));
const GiftCardRedeemPage = lazy(() => import('./pages/GiftCardRedeemPage'));
const ResolveTablePage = lazy(() => import('./pages/ResolveTablePage'));
const QrBillPage = lazy(() => import('./pages/QrBillPage'));
const LoyaltyPage = lazy(() => import('./pages/LoyaltyPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ReservationBookingPage = lazy(() => import('./pages/ReservationBookingPage'));
const NpsSurveyPage = lazy(() => import('./pages/NpsSurveyPage'));
const WaitingForCashPage = lazy(() => import('./pages/WaitingForCashPage'));
const DemoRedirectPage = lazy(() => import('./pages/DemoRedirectPage'));
// Vague 2026-05-30 features 8 + 20: live order tracking + order-ahead flow.
const OrderTrackingPage = lazy(() => import('./pages/OrderTrackingPage'));
const OrderAheadPage = lazy(() => import('./pages/OrderAheadPage'));

export default function App() {
  // QR-scan PWA flow has no explicit signup. We create an anonymous Supabase
  // session on first load so edge functions that require auth.getUser()
  // (split-order, etc.) work transparently. The user can later link this
  // session to an email/phone for cross-device history.
  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!data.session) {
        const { error } = await supabase.auth.signInAnonymously();
        if (error) console.warn('[scanpay-web] anon sign-in failed:', error.message);
      }
    })();
  }, []);

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <Routes>
          {/* TapNpay PWA routes: /:storeId/:tableId is the QR/NFC entry point */}
          <Route path="/:storeId/:tableId" element={<MenuPage />} />
          <Route path="/:storeId/:tableId/cart" element={<CartPage />} />
          <Route path="/:storeId/:tableId/checkout" element={<CheckoutPage />} />
          <Route path="/:storeId/:tableId/checkout/split" element={<SplitBillModePage />} />
          <Route path="/:storeId/:tableId/checkout/split/equal" element={<SplitEqualPage />} />
          <Route path="/:storeId/:tableId/checkout/split/by-item" element={<SplitByItemPage />} />
          <Route path="/:storeId/:tableId/checkout/split/custom" element={<SplitCustomPage />} />
          <Route path="/:storeId/:tableId/confirmation" element={<ConfirmationPage />} />
          <Route path="/:storeId/:tableId/cash-wait" element={<WaitingForCashPage />} />
          <Route path="/:storeId/:tableId/my-orders" element={<MyOrdersPage />} />
          <Route path="/:storeId/:tableId/history" element={<OrderHistoryPage />} />
          {/* Live per-item progress (pending → preparing → ready → served). */}
          <Route path="/:storeId/:tableId/track" element={<OrderTrackingPage />} />
          <Route path="/:storeId/:tableId/review" element={<ReviewPromptPage />} />
          {/* eNPS survey — Closes [GAP-1 Phase 1 Vague 1.A]: NpsSurveyPage was
              orphaned (component existed, no Route → unreachable). Now wired
              from ConfirmationPage's secondary CTA. */}
          <Route path="/:storeId/:tableId/nps" element={<NpsSurveyPage />} />
          {/* Multi-store profile-level history (signed-in user) */}
          <Route path="/history" element={<OrderHistoryPage />} />
          {/* NFC entry — pulse-animation page that scans then redirects. */}
          <Route path="/nfc/:storeId/:tableId" element={<NFCWaitingPage />} />
          {/* Standalone customer flow for purchasing a gift card. */}
          <Route path="/gift-cards/:storeId" element={<GiftCardPurchasePage />} />
          {/* Gift card redemption: from email (/gift/:code) or in-cart (/:storeId/gift/:code). */}
          <Route path="/gift/:code" element={<GiftCardRedeemPage />} />
          <Route path="/:storeId/gift/:code" element={<GiftCardRedeemPage />} />
          <Route path="/:storeId/gift" element={<GiftCardRedeemPage />} />
          {/* QR / NFC sticker entry — token resolves to (store, table) via edge fn. */}
          <Route path="/t/:token" element={<ResolveTablePage />} />
          {/* Sunday-style QR-on-Bill — printed at the table by the waiter. */}
          <Route path="/b/:token" element={<QrBillPage />} />
          {/* Loyalty / Profile (V1.5 — backend partial). */}
          <Route path="/loyalty" element={<LoyaltyPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          {/* Customer-facing reservation booking (V1.5). */}
          <Route path="/:storeId/book" element={<ReservationBookingPage />} />
          {/* Order-ahead / takeout flow — gated server-side by
              tenant_feature `order_ahead`. */}
          <Route path="/:storeId/order-ahead" element={<OrderAheadPage />} />
          {/* Demo shortcut — clean URL without raw UUIDs for presentations. */}
          <Route path="/demo" element={<DemoRedirectPage />} />
          <Route path="/" element={<NotFoundPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <InstallPrompt />
    </ErrorBoundary>
  );
}
